/**
 * Кешът на самоличността (docs/auth-access.md §Кеш на самоличността,
 * docs/edge.md): последният успешен отговор на `GET /user/info` се пази в
 * `localStorage["auth_user_info:<sha256 префикс на токена>"]` — целият
 * `UserInfoResponse`, по един запис за всеки токен (X на терминала при
 * личен акаунт, Y на служителя при общ терминал).
 *
 * Защо: reload без интернет иначе губи ролите — единственият `fetchUserInfo`
 * при старт пада с мрежова грешка и `AuthorizedRoute` праща към „Модули".
 * С кеша Redux се хидратира ВЕДНАГА при старт (`main.tsx`,
 * `restoreEmployeeSession`), а `fetchUserInfo` само опреснява на заден план.
 *
 * Жизнен цикъл: пише се при всеки успешен `GET /user/info`; трие се за
 * доказано мъртъв токен (401/403 от облака — `authSlice.fetchUserInfo`,
 * `lib/sessionRecovery.ts`) и заедно с носителя си (`lib/auth.ts`:
 * `clearToken`, `clearEmployeeRecord*`). Мрежова грешка НЕ го пипа.
 *
 * Модулът е чист (само storage + sha256), за да го ползват и `lib/auth.ts`,
 * и `authSlice` без цикъл.
 */
import type { UserInfoResponse } from "../api/auth";
import { sha256Hex } from "./sha256";

const USER_INFO_CACHE_PREFIX = "auth_user_info:";
/** 16 hex знака (64 бита) от SHA-256 стигат срещу сблъсък и не носят токена. */
const TOKEN_HASH_PREFIX_LENGTH = 16;

export const userInfoCacheKey = (token: string): string =>
  `${USER_INFO_CACHE_PREFIX}${sha256Hex(token).slice(0, TOKEN_HASH_PREFIX_LENGTH)}`;

const storageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const storageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage недостъпен (private mode, квота) — просто не помним */
  }
};
const storageRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage недостъпен */
  }
};

/** Минималната форма, без която хидратираният Redux би счупил guard-овете. */
function isUserInfoShape(value: unknown): value is UserInfoResponse {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<UserInfoResponse>;
  return (
    typeof candidate.id === "number" &&
    typeof candidate.username === "string" &&
    typeof candidate.personal === "boolean" &&
    Array.isArray(candidate.roles) &&
    Array.isArray(candidate.permissions)
  );
}

export function readCachedUserInfo(
  token: string | null | undefined,
): UserInfoResponse | null {
  if (!token) return null;
  const raw = storageGet(userInfoCacheKey(token));
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isUserInfoShape(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeCachedUserInfo(token: string, info: UserInfoResponse) {
  storageSet(userInfoCacheKey(token), JSON.stringify(info));
}

export function clearCachedUserInfo(token: string | null | undefined) {
  if (!token) return;
  storageRemove(userInfoCacheKey(token));
}

/** Всички записи — при пълен изход от устройството. */
export function clearAllCachedUserInfo() {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(USER_INFO_CACHE_PREFIX)) keys.push(key);
    }
  } catch {
    return;
  }
  for (const key of keys) storageRemove(key);
}
